<template>
  <div class="contact-info">
    <div class="section">
      <h3 class="section__title">Confirm your contact info</h3>
      <v-layout wrap>
        <div class="row">
          <v-text-field
            v-model="model.givenName"
            :rules="nameRules"
            label="First name *"
            required
            type="text"
            autocomplete="given-name"
            name="given-name">
          </v-text-field>

          <v-text-field
            v-model="model.familyName"
            :rules="nameRules"
            label="Last name *"
            required
            autocomplete="family-name"
            name="family-name">
          </v-text-field>
        </div>

        <div class="row">
          <v-text-field
            v-model="model.primaryEmail"
            :rules="emailRules"
            label="Email *"
            autocomplete="email"
            name="email"
            required>
          </v-text-field>
        </div>

        <div class="row">
          <v-text-field
            v-model="model.phoneNumber"
            :rules="phoneRules"
            label="Phone number"
            mask="###-###-####"
            autocomplete="tel-national"
            name="tel-national">
          </v-text-field>

          <v-select
            v-model="model.phoneType"
            label="Phone type"
            :hide-details="true"
            :items="phoneTypes"
            :menu-props="{ auto: true }"
            ref="select-input-common" />
        </div>
      </v-layout>
    </div>
    <div class="section" v-if="showOptionalField">
      <h3 class="section__title">
        Home Inspection Details
        <span class="section__title--gray">(Optional)</span>
      </h3>
      <div class="field">
        <div class="field__title">
          Do you have an expected closing date for the property being inspected?
        </div>
        <div class="row">
          <v-datepicker
            v-model="model.date"
            label="Select Date"/>
        </div>
      </div>
      <div class="field">
        <div class="field__title">
          Are you a first time home buyer?
        </div>
        <div class="row">
          <v-radio-group v-model="model.firstTime" row>
            <v-radio
              label="No"
              :value="false"></v-radio>

            <v-radio
              label="Yes"
              :value="true"></v-radio>
          </v-radio-group>
        </div>
      </div>
    </div>
    <div class="section" v-if="showOptionalField">
      <h3 class="section__title">
        Current Address
        <span class="section__title--gray">(Optional)</span>
      </h3>
      <div class="address-layout">
        <div class="row">
          <v-text-field
            v-model="model.address1"
            label="Address 1">
          </v-text-field>
        </div>

        <div class="row">
          <v-text-field
            v-model="model.address2"
            label="Address 2">
          </v-text-field>
        </div>

        <div class="row">
          <v-text-field
            v-model="model.city"
            label="City">
          </v-text-field>

          <v-select
            v-model="model.state"
            label="State"
            :hide-details="true"
            :items="states"
            :menu-props="{ auto: true }"
            ref="select-input-common" />

          <v-text-field
            v-model="model.postalCode"
            label="ZIP/Postal Code">
          </v-text-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DatePicker from '@/components/Shared/DatePicker.vue';
import AccountClient from '@/utilities/account-client';
import { states } from '@/constants/states';

export default {
  components: {
    'v-datepicker': DatePicker,
  },
  data: () => ({
    phoneTypes: ['Work', 'Mobile', 'Home'],
    phoneTypesMapper: {
      workPhone: 'Work',
      mobilePhone: 'Mobile',
      homePhone: 'Home',
    },
    states,
    model: {
      givenName: '',
      familyName: '',
      primaryEmail: '',
      phoneType: '',
      phoneNumber: '',
      date: '',
      firstTime: false,
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
    },
    nameRules: [
      v => !!v || 'Name is required',
      v => v.length <= 40 || 'Name must be less than 40 characters',
    ],
    phoneRules: [
      v => (v.length === 0 || v.length === 10) || 'Phone must be equal 10 digits',
    ],
    phoneTypeRules: [
      v => !!v || 'Phone type is required',
    ],
    emailRules: [
      v => !!v || 'Please enter correct email format',
      v => /.+@.+/.test(v) || 'Please enter correct email format',
    ],
  }),
  computed: {
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    showOptionalField() {
      return !this.enabledFeatures['WEB-5298-Welcome-Flow-Hide-Optional-Fields'];
    },
  },
  methods: {
    initializeClients() {
      this.clients.accountClient = new AccountClient(
        process.env.VUE_APP_ROOT_API,
        this.userAuthToken,
      );
    },
  },
  watch: {
    model: {
      handler(value) {
        this.$emit('on-form-change', value);
      },
      deep: true,
    },
  },
  mounted() {
    const { currentUser } = this.$store.state.User;
    let phone = '';
    // eslint-disable-next-line no-nested-ternary
    phone = currentUser.mobilePhone ? this.phoneTypes[1] : (currentUser.homePhone)
      ? this.phoneTypes[2] : (currentUser.workPhone) ? this.phoneTypes[0] : this.phoneTypes[1];
    this.model = {
      givenName: currentUser.givenName,
      familyName: currentUser.familyName,
      primaryEmail: currentUser.primaryEmail,
      phoneType: phone,
      phoneNumber: currentUser.mobilePhone || currentUser.homePhone || currentUser.workPhone,
      address1: currentUser.primaryAddress.address1,
      address2: currentUser.primaryAddress.address2,
      city: currentUser.primaryAddress.city,
      state: currentUser.primaryAddress.state,
      postalCode: currentUser.primaryAddress.postalCode,
    };
    if (this.model.phoneNumber) {
      this.model.phoneNumber = this.model.phoneNumber.replace(/^\+1/, '');
    }
    if (this.model.phoneNumber && this.model.phoneNumber.startsWith('1')) {
      this.model.phoneNumber = this.model.phoneNumber.substr(1);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.section {
  padding-bottom: 31px;
  border-bottom: 1px solid $theme-color-gray-light;

  &__title {
    margin-top: 32px;
    font-family: 'Barlow Condensed', sans-serif;
    letter-spacing: 0.14px;
    font-size: 24px;
    font-weight: 400;
    text-transform: uppercase;

    &--gray {
      color: #aaa;
    }
  }

  &:nth-child(3) {
    border-bottom: none;
    margin-bottom: -10px;
  }
}

.field {
  &__title {
    margin-top: 24px;
    font-size: 18px;
    font-weight: 500;
  }
}

.layout {
  max-width: 424px;
}

.row {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 5px;
}

.address-layout {
  max-width: 632px;

  .row:nth-child(3) {
    gap: 16px;

    .v-input {
      &:nth-child(1) {
        width: 250px;
      }

      &:nth-child(2), &:nth-child(3) {
        width: 175px;
      }
    }
  }
}

@media all and (max-width: 655px) {
  .section {
    padding-bottom: 15px;

    &:nth-child(3) {
      margin-bottom: 0;
    }
  }

  .v-input {
    width: 50%;
  }

  .field {
    &__title {
      font-size: 16px;
    }
  }

  .layout {
    max-width: initial;
  }

  .address-layout {
    max-width: initial;

    .row:nth-child(3) {
      flex-wrap: wrap;

      .v-input {
        &:nth-child(1) {
          width: 100%;
        }

        &:nth-child(2), &:nth-child(3) {
          max-width: calc(50% - 9px);
        }
      }
    }
  }
}
</style>
